import { GroupHitbox, type Hitbox, HitboxType, RectangleHitbox, type ShapeHitbox } from "./hitbox";
import { Collision, type CollisionResponse, Geometry, type IntersectionResponse } from "./math";
import { buildOperatorRig, type OperatorRigState } from "./operatorRig";
import { Vec, type Vector } from "./vector";
import { OperatorCollisionRig } from "./operatorCollisionRig";
import { operatorSkin } from "../definitions/operatorSkins";

export const OPERATOR_DISABLED = 255;
// Operator 37: 25% fewer leg cycles per metre than operator 36. Travel speed is unchanged.
export const OPERATOR_CYCLE_DISTANCE = 5.2 / (0.75 * 0.8 * 0.75);
export function operatorPose(_rotation: number, stride: number): number {
    return Math.floor(((stride / OPERATOR_CYCLE_DISTANCE) % 1 + 1) % 1 * 254);
}

/** Exact convex mesh projection. The rectangle is used for broad phase only. */
export class OperatorPartHitbox extends RectangleHitbox {
    constructor(readonly points: Vector[]) {
        super(Vec(Math.min(...points.map(p => p.x)), Math.min(...points.map(p => p.y))),
            Vec(Math.max(...points.map(p => p.x)), Math.max(...points.map(p => p.y))));
    }
    override isPointInside(point: Vector): boolean {
        return this.points.every((a, i) => {
            const b = this.points[(i + 1) % this.points.length];
            return (b.x - a.x) * (point.y - a.y) - (b.y - a.y) * (point.x - a.x) >= -1e-9;
        });
    }
    private nearest(point: Vector): Vector {
        let nearest = this.points[0], distance = Infinity;
        for (let i = 0; i < this.points.length; i++) {
            const a = this.points[i], b = this.points[(i + 1) % this.points.length], d = Vec.sub(b, a);
            const t = Math.max(0, Math.min(1, ((point.x - a.x) * d.x + (point.y - a.y) * d.y) / (d.x * d.x + d.y * d.y || 1)));
            const q = Vec.add(a, Vec.scale(d, t)), next = Geometry.distanceSquared(q, point);
            if (next < distance) { nearest = q; distance = next; }
        }
        return nearest;
    }
    override narrowIntersection(that: ShapeHitbox): CollisionResponse {
        if (that.type === HitboxType.Circle) {
            const point = this.nearest(that.position);
            const distance = Geometry.distance(point, that.position), inside = this.isPointInside(that.position);
            if (!inside && distance > that.radius) return null;
            const dir = Vec.normalize(inside ? Vec.sub(point, that.position) : Vec.sub(that.position, point));
            return { dir: distance < 1e-9 ? Vec(1, 0) : dir, pen: inside ? that.radius + distance : that.radius - distance };
        }
        const other = that instanceof OperatorPartHitbox ? that.points : [that.min, Vec(that.max.x, that.min.y), that.max, Vec(that.min.x, that.max.y)];
        let penetration = Infinity, direction = Vec(1, 0);
        for (const polygon of [this.points, other]) for (let i = 0; i < polygon.length; i++) {
            const a = polygon[i], b = polygon[(i + 1) % polygon.length];
            const axis = Vec.normalize(Vec(-(b.y - a.y), b.x - a.x));
            const first = this.points.map(p => p.x * axis.x + p.y * axis.y), second = other.map(p => p.x * axis.x + p.y * axis.y);
            const overlap = Math.min(Math.max(...first), Math.max(...second)) - Math.max(Math.min(...first), Math.min(...second));
            if (overlap < 0) return null;
            if (overlap < penetration) { penetration = overlap; direction = axis; }
        }
        const delta = Vec.sub(that.getCenter(), this.getCenter());
        if (delta.x * direction.x + delta.y * direction.y < 0) direction = Vec.invert(direction);
        return { dir: direction, pen: penetration };
    }
    override getIntersection(that: Hitbox): CollisionResponse {
        if (that.type === HitboxType.Circle || that.type === HitboxType.Rect) return this.narrowIntersection(that);
        return super.getIntersection(that);
    }
    override collidesWith(that: Hitbox): boolean {
        if (that.type === HitboxType.Group) return that.hitboxes.some(h => this.collidesWith(h));
        if (that.type === HitboxType.Polygon) return that.points.some(p => this.isPointInside(p)) || this.points.some(p => that.isPointInside(p));
        return this.narrowIntersection(that) !== null;
    }
    override intersectsLine(a: Vector, b: Vector): IntersectionResponse {
        if (this.isPointInside(a)) return { point: Vec.clone(a), normal: Vec.normalize(Vec.sub(a, b)) };
        let best: IntersectionResponse = null, distance = Infinity;
        for (let i = 0; i < this.points.length; i++) {
            const c = this.points[i], d = this.points[(i + 1) % this.points.length];
            const point = Collision.lineIntersectsLine(a, b, c, d);
            if (!point) continue;
            const next = Geometry.distanceSquared(a, point);
            if (next < distance) { best = { point, normal: Vec.normalize(Vec(d.y - c.y, c.x - d.x)) }; distance = next; }
        }
        return best;
    }
    override clone(): OperatorPartHitbox { return new OperatorPartHitbox(this.points.map(p => Vec.clone(p))); }
    override toRectangle(): RectangleHitbox { return new RectangleHitbox(this.min, this.max); }
    override randomPoint(): Vector { const center = this.getCenter(); return this.isPointInside(center) ? center : this.points[0]; }
}

export class OperatorDamageHitbox extends GroupHitbox<OperatorPartHitbox[]> {
    override intersectsLine(a: Vector, b: Vector): IntersectionResponse {
        let best: IntersectionResponse = null, distance = Infinity;
        for (const part of this.hitboxes) {
            const hit = part.intersectsLine(a, b);
            if (!hit) continue;
            const next = Geometry.distanceSquared(a, hit.point);
            // Strict comparison preserves the original stable order for equal contacts.
            if (next < distance) { best = hit; distance = next; }
        }
        return best;
    }
}
export function createOperatorHandHitbox(state: OperatorRigState, position: Vector, size = 1): OperatorDamageHitbox {
    const hand = state.gesture === "punchLeft" ? "glove-1" : "glove1";
    return new OperatorDamageHitbox(...buildOperatorRig(state, false).filter(part => part.name === hand).flatMap(part =>
        (part.convex ? [part.hull] : part.surfaces.map(face => face.points)).map(points =>
            new OperatorPartHitbox(points.map(point => Vec.add(position, Vec.scale(point, size))))
        )
    ));
}
export function createOperatorHitbox(state: OperatorRigState, position: Vector, size = 1, workspace?: OperatorCollisionRig): OperatorDamageHitbox {
    // Paid cosmetics use the base operator's damage silhouette, never their decorative armour.
    if (operatorSkin(state.skinId).priceUSD) state = { ...state, skinId: "raider_cobalt" };
    return new OperatorDamageHitbox(...buildOperatorRig(state, false, true, workspace).flatMap(part =>
        (part.convex ? [part.hull] : part.surfaces.map(face => face.points)).map(points =>
            new OperatorPartHitbox(points.map(point => Vec.add(position, Vec.scale(point, size))))
        )
    ));
}
export class OperatorHitboxCache {
    private readonly workspace = new OperatorCollisionRig();
    private key = "";
    private shape?: OperatorDamageHitbox;
    get(state: OperatorRigState, position: Vector, size: number): OperatorDamageHitbox {
        const key = `${JSON.stringify(state)}:${position.x}:${position.y}:${size}`;
        if (key !== this.key || !this.shape) { this.key = key; this.shape = createOperatorHitbox(state, position, size, this.workspace); }
        return this.shape;
    }
}
export function operatorBounds(position: Vector, size: number): RectangleHitbox {
    return new RectangleHitbox(Vec(position.x - 8 * size, position.y - 8 * size), Vec(position.x + 8 * size, position.y + 8 * size));
}
